import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import TextInput from '../../../components/ui/TextInput.jsx';
import Button from '../../../components/ui/Button';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 440px;
  max-width: 92vw;
  padding: 24px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const CurrentEmployee = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
  margin-bottom: 16px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #E53935;
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

// Faz B (spec 2026-08-07 §4, Task 5) — taşıma modalı: yalnız moderatörse
// SessionGridPage tarafından açılır (BE zaten reddeder — bkz. MoveAsync
// notModerator — FE yalnız UX). Hedef seviye küçük/sabit bir liste olduğundan
// (session grid'in kendi lane'leri) AsyncGenericSelect DEĞİL GenericSelect
// kullanılır — arama gerektirmez.
export default function MoveModal({ entry, levels, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [toLevelId, setToLevelId] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const levelOptions = (levels || []).map((l) => ({ id: l.levelId, name: l.label }));

  const handleSubmit = async () => {
    if (!toLevelId) {
      setError(t('performance.calibrationSession.moveModal.levelRequiredHint'));
      return;
    }
    if (!note.trim()) {
      setError(t('performance.calibrationSession.moveModal.noteRequiredHint'));
      return;
    }
    setError('');
    setSaving(true);
    const result = await onSubmit({
      calibrationRecordId: entry.calibrationRecordId,
      toLevelId,
      note: note.trim(),
      // Analitik Plan 5 — ızgarada okunan sürüm; başkası taşıdıysa sunucu 409 versionConflict döner.
      expectedVersion: entry.version,
    });
    setSaving(false);
    if (result.ok) {
      onClose();
      return;
    }
    setError(t(`responseErrors.${result.message}`, { defaultValue: result.message }));
  };

  return (
    <Overlay onClick={onClose}>
      <Modal data-role="move-modal" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('performance.calibrationSession.moveModal.title')}</Title>
          <CloseBtn onClick={onClose} disabled={saving}><X size={18} /></CloseBtn>
        </Header>

        <CurrentEmployee>{entry.employeeName}</CurrentEmployee>

        <Field>
          <GenericSelect
            label={t('performance.calibrationSession.moveModal.targetLevel')}
            options={levelOptions}
            value={toLevelId}
            onChange={setToLevelId}
            idKey="id"
            labelKey="name"
            required
          />
        </Field>

        <Field>
          <TextInput
            label={t('performance.calibrationSession.moveModal.note')}
            type="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
          />
        </Field>

        {error && <ErrorText data-role="move-error">{error}</ErrorText>}

        <Footer>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button data-role="move-submit-btn" onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
